import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Stack,
  Chip,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  MergeType as MergeTypeIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { locationApi } from '../services/locationApi';

// ---------------------------------------------------------------------------
// Edit / Approve dialog
// ---------------------------------------------------------------------------

function EditApproveDialog({ open, city, onClose, onApproved }) {
  const [nameEn, setNameEn] = useState('');
  const [nameHe, setNameHe] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (city) {
      setNameEn(city.nameEn ?? '');
      setNameHe(city.nameHe ?? '');
      setLatitude(city.latitude != null ? String(city.latitude) : '');
      setLongitude(city.longitude != null ? String(city.longitude) : '');
      setError('');
    }
  }, [city]);

  const handleApprove = async () => {
    setSaving(true);
    setError('');
    try {
      const updated = await locationApi.approveCity(city.id, {
        nameEn: nameEn.trim() || null,
        nameHe: nameHe.trim() || null,
        latitude: latitude.trim() || null,
        longitude: longitude.trim() || null,
      });
      // Pass the full DTO back so the parent can refresh the row in-place
      // (otherwise the cached row keeps showing the old nameHe / nameEn).
      onApproved(updated && updated.id ? updated : { id: city.id, verified: true });
      onClose();
    } catch (e) {
      setError('Failed to approve. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!city) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit &amp; Approve City #{city.id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name (English)"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            fullWidth
          />
          <TextField
            label="Name (Hebrew)"
            value={nameHe}
            onChange={(e) => setNameHe(e.target.value)}
            dir="rtl"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Latitude"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              fullWidth
            />
            <TextField
              label="Longitude"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          onClick={handleApprove}
          disabled={saving || (!nameEn.trim() && !nameHe.trim())}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bulk merge dialog — pick a target (must be verified, same country)
// ---------------------------------------------------------------------------

function BulkMergeDialog({ open, sources, onClose, onMerged }) {
  const [target, setTarget] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The country must be consistent across sources for the bulk merge to be
  // legal (CityMergeService rejects cross-country merges).
  const countryIds = useMemo(
    () => Array.from(new Set(sources.map((c) => c.countryId).filter((x) => x != null))),
    [sources],
  );
  const singleCountryId = countryIds.length === 1 ? countryIds[0] : null;

  useEffect(() => {
    if (!open || singleCountryId == null) {
      setOptions([]);
      return;
    }
    setLoading(true);
    locationApi
      .getCitiesByCountryId(singleCountryId)
      .then((cities) => setOptions(Array.isArray(cities) ? cities : []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [open, singleCountryId]);

  useEffect(() => {
    if (!open) {
      setTarget(null);
      setError('');
    }
  }, [open]);

  const sourceIds = useMemo(() => sources.map((c) => c.id), [sources]);
  const validTargetOptions = useMemo(
    () => options.filter((c) => !sourceIds.includes(c.id)),
    [options, sourceIds],
  );

  const handleConfirm = async () => {
    if (!target) return;
    setSaving(true);
    setError('');
    try {
      await locationApi.bulkMergeCities(sourceIds, target.id);
      onMerged(sourceIds);
      onClose();
    } catch (e) {
      setError(e?.message || 'Failed to merge. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Merge {sources.length} cities into one</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {singleCountryId == null && sources.length > 0 && (
            <Alert severity="warning">
              Selected cities span multiple countries — backend rejects cross-country merges.
              Narrow your selection to one country.
            </Alert>
          )}
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Sources:
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
              {sources.map((c) => (
                <Chip
                  key={c.id}
                  size="small"
                  label={`#${c.id} ${c.nameEn || c.nameHe || ''}`}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
          <Autocomplete
            options={validTargetOptions}
            value={target}
            onChange={(_, v) => setTarget(v)}
            getOptionLabel={(c) =>
              c ? `${c.nameEn || ''}${c.nameHe ? ` / ${c.nameHe}` : ''} (#${c.id})` : ''
            }
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            loading={loading}
            disabled={singleCountryId == null}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Merge target (canonical city)"
                placeholder="Pick the city all sources will fold into"
                helperText="Only cities in the same country are eligible. Pick a verified row for best results."
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<MergeTypeIcon />}
          onClick={handleConfirm}
          disabled={saving || !target || singleCountryId == null}
        >
          {saving ? 'Merging…' : `Merge ${sources.length} → target`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const VERIFIED_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Verified only', value: 'true' },
  { label: 'Unverified only', value: 'false' },
];

const CitiesManagement = () => {
  const navigate = useNavigate();

  // Filters
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(null);
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // debounced

  // Data
  const [cities, setCities] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dialogs
  const [editOpen, setEditOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingCity, setDeletingCity] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Load countries once
  useEffect(() => {
    locationApi
      .getAllCountries()
      .then((list) => setCountries(Array.isArray(list) ? list : []))
      .catch(() => setCountries([]));
  }, []);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const verifiedValue =
    verifiedFilter === 'true' ? true : verifiedFilter === 'false' ? false : null;

  const fetchCities = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await locationApi.adminListCities({
        countryId: country?.id ?? null,
        q: searchQuery,
        verified: verifiedValue,
        page,
        size: rowsPerPage,
      });
      setCities(Array.isArray(data?.content) ? data.content : []);
      setTotalElements(data?.totalElements ?? 0);
    } catch (e) {
      setError('Failed to load cities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [country?.id, searchQuery, verifiedValue, page, rowsPerPage]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [country?.id, searchQuery, verifiedFilter]);

  // Reset selection when the underlying list changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [country?.id, searchQuery, verifiedFilter, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === cities.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(cities.map((c) => c.id)));
    }
  };

  const removeCityLocal = (id) => {
    setCities((prev) => prev.filter((c) => c.id !== id));
    setTotalElements((prev) => Math.max(0, prev - 1));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // Edit / approve
  const handleOpenEdit = (city) => {
    setEditingCity(city);
    setEditOpen(true);
  };
  const handleCloseEdit = () => setEditOpen(false);
  const onApproved = (updated) => {
    // The dialog hands back the full DTO from the PATCH response so we can
    // refresh nameEn / nameHe / coords in place — flipping `verified` alone
    // would leave the cached names stale.
    setCities((prev) =>
      prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
    );
  };

  // Delete
  const handleOpenDelete = (city) => {
    setDeletingCity(city);
    setDeleteError('');
    setDeleteOpen(true);
  };
  const handleCloseDelete = () => {
    if (deletingBusy) return;
    setDeleteOpen(false);
  };
  const handleConfirmDelete = async () => {
    if (!deletingCity) return;
    setDeletingBusy(true);
    setDeleteError('');
    try {
      await locationApi.deleteCity(deletingCity.id);
      removeCityLocal(deletingCity.id);
      setDeleteOpen(false);
    } catch (e) {
      setDeleteError(e?.message || 'Failed to delete city.');
    } finally {
      setDeletingBusy(false);
    }
  };

  // Bulk merge
  const selectedCities = useMemo(
    () => cities.filter((c) => selectedIds.has(c.id)),
    [cities, selectedIds],
  );
  const handleOpenBulkMerge = () => setMergeOpen(true);
  const handleCloseBulkMerge = () => setMergeOpen(false);
  const handleBulkMerged = (mergedIds) => {
    setCities((prev) => prev.filter((c) => !mergedIds.includes(c.id)));
    setTotalElements((prev) => Math.max(0, prev - mergedIds.length));
    setSelectedIds(new Set());
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 2 }}
      >
        Dashboard
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Cities Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search across all cities (verified + unverified), curate, and merge duplicates.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={fetchCities} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<MergeTypeIcon />}
            onClick={handleOpenBulkMerge}
            disabled={selectedIds.size < 1}
          >
            Merge selected ({selectedIds.size})
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Autocomplete
            options={countries}
            value={country}
            onChange={(_, v) => setCountry(v)}
            getOptionLabel={(c) =>
              c ? `${c.nameEn || ''}${c.nameHe ? ` / ${c.nameHe}` : ''}` : ''
            }
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            sx={{ flex: 1, minWidth: 240 }}
            renderInput={(params) => (
              <TextField {...params} label="Country (optional filter)" />
            )}
          />
          <TextField
            label="Search (fuzzy)"
            placeholder="e.g. Herzlia, תל אביב, jeru"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={verifiedFilter}
              label="Status"
              onChange={(e) => setVerifiedFilter(e.target.value)}
            >
              {VERIFIED_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.size > 0 && selectedIds.size < cities.length}
                    checked={cities.length > 0 && selectedIds.size === cities.length}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Name (en)</TableCell>
                <TableCell>Name (he)</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Lat / Lng</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No cities found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                cities.map((city) => {
                  const isSel = selectedIds.has(city.id);
                  return (
                    <TableRow key={city.id} hover selected={isSel}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSel}
                          onChange={() => toggleSelect(city.id)}
                        />
                      </TableCell>
                      <TableCell>{city.id}</TableCell>
                      <TableCell>{city.nameEn || '—'}</TableCell>
                      <TableCell dir="rtl">{city.nameHe || '—'}</TableCell>
                      <TableCell>{city.countryName || `#${city.countryId ?? '?'}`}</TableCell>
                      <TableCell>
                        {city.verified ? (
                          <Chip
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                            label="Verified"
                          />
                        ) : (
                          <Chip size="small" color="warning" label="Unverified" />
                        )}
                      </TableCell>
                      <TableCell>
                        {city.latitude && city.longitude
                          ? `${city.latitude}, ${city.longitude}`
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit / Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenEdit(city)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete (only if unreferenced)">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDelete(city)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </TableContainer>
      )}

      <EditApproveDialog
        open={editOpen}
        city={editingCity}
        onClose={handleCloseEdit}
        onApproved={onApproved}
      />

      <BulkMergeDialog
        open={mergeOpen}
        sources={selectedCities}
        onClose={handleCloseBulkMerge}
        onMerged={handleBulkMerged}
      />

      <Dialog open={deleteOpen} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete city</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Permanently delete{' '}
            <strong>
              {deletingCity?.nameEn || deletingCity?.nameHe || `#${deletingCity?.id}`}
            </strong>
            ? The backend rejects the delete if any user, garden, group or
            visibility row still points at it — in that case, merge it into a
            canonical city first.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deletingBusy}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deletingBusy}
          >
            {deletingBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CitiesManagement;
